import * as React from 'react';
import { Component } from 'react';
import {
  LayoutAnimation, StyleSheet, View, ScrollView, UIManager, Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import ExpandableItemComponent from './ExpandableItemComponent';
import FlagIcon from './FlagIcon';
import SmileIcon from './SmileIcon';
import CheckCircle from './CheckCircle';
import GridIcon from './24pxIcons/GridIcon';
import MapIcon from './MapIcon';
import BoltIconDesktop from './24pxIcons/BoltIconDesktop';
import ChairIconDesktop from './24pxIcons/ChairIconDesktop';
import MonitorIcon from './MonitorIcon';
import SunIcon from './SunIcon';
import SolveIcon24 from './24pxIcons/SolveIcon24';
import PeopleIcon24 from './24pxIcons/PeopleIcon24';
import OccupancyIcon from './24pxIcons/OccupancyIcon';
import HomeIconDesktop from './24pxIcons/HomeIconDesktop';
import BotIcon from './24pxIcons/BotIcon';
import SupportIcon from './24pxIcons/SupportIcon';
import { HappyScoreCategory, HappyScoreCategoryTitles, UserPermission } from '../constants/Types';
import { AppScreen } from '../navigation/LinkingConfiguration';
import Colors from '../constants/Colors';
import ThermometerIcon from './24pxIcons/ThermometerIcon';
import SensorIcon24 from './24pxIcons/SensorIcon24';

interface Props {
  navObj: any;
  initialState: any;
  collapsed: boolean;
  isAdmin: boolean;
  permissions: UserPermission[];
  enabledFeatures?: { [key: string]: boolean };
}

interface State {
  listDataSource: any;
  selected: string;
  isDev: boolean;
}

export default class ExpandableDrawer extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    let happyEnabled: boolean = false;
    this.state = { listDataSource: [], selected: '', isDev: false };
    AsyncStorage.getItem('happyEnabled').then((value) => {
      happyEnabled = value === 'enabled';
      AsyncStorage.getItem('apiDomain').then((domain: string) => {
        const isDev: boolean = (domain === 'api-v2-dev2.hereworks.tech');
        const listDataSource = happyEnabled ? CONTENT : SOLVE_CONTENT;
        const filteredItems = listDataSource.map((category) => ({
          ...category,
          subcategory: category.subcategory.filter(
            (item) => props.isAdmin
              || !item.requiredPermissions
              || item.requiredPermissions
                .some((requiredPermission: UserPermission) => props.permissions
                  .includes(requiredPermission))
              || (item.featureFlag
                && (props.enabledFeatures
                  && props.enabledFeatures[item.featureFlag])),
          ),
        }));
        this.setState({
          selected: '', isDev, listDataSource: filteredItems,
        });
      });
    });
    if (Platform.OS === 'android') {
      UIManager.setLayoutAnimationEnabledExperimental(true);
    }
  }

  updateLayout = (index: number) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    const array = [...this.state.listDataSource];
    array[index].isExpanded = !array[index].isExpanded;
    this.setState(() => ({
      listDataSource: array,
    }));
  };

  updateCategory = (selected: string) => {
    this.setState(() => ({
      selected,
    }));
  };

  render() {
    return (
      <View style={styles.container}>
        <ScrollView style={{}}>
          {this.state.listDataSource.map((item: any, key: number) => (
            <ExpandableItemComponent
              key={`item_${key}`}
              item={item}
              navObj={this.props.navObj}
              initialState={this.props.initialState}
              isDev={this.state.isDev}
              collapsed={this.props.collapsed}
              enabledFeatures={this.props.enabledFeatures}
            />
          ))}
        </ScrollView>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    paddingTop: 0,
    minHeight: 400,
  },
});

const CONTENT = [
  {
    isExpanded: true,
    category_name: 'MANAGE',
    subcategory: [
      {
        id: AppScreen.HOME,
        val: AppScreen.HOME,
        icon: <HomeIconDesktop />,
        selectedIcon: <HomeIconDesktop color={'#fff'} />,
        testID: 'home-section-btn',
      },
      {
        id: AppScreen.HAPPY_CHAT_V2,
        val: AppScreen.HAPPY_CHAT_V2,
        title: 'Insights Agent',
        icon: <BotIcon />,
        selectedIcon: <BotIcon color={'#fff'} />,
        featureFlag: 'chatbot',
        testID: 'happy-chat-v2-section-btn',
        beta: true,
      },
      {
        id: AppScreen.TICKETS,
        val: AppScreen.TICKETS,
        icon: <FlagIcon />,
        selectedIcon: <FlagIcon color={'#fff'} />,
        testID: 'tickets-section-btn',
        requiredPermissions: [UserPermission.TICKETS_READ],
      },
      {
        id: AppScreen.TICKETS_V2,
        val: AppScreen.TICKETS_V2,
        title: 'Tickets V2',
        icon: <FlagIcon />,
        selectedIcon: <FlagIcon color={'#fff'} />,
        beta: true,
        testID: 'tickets-v2-section-btn',
        requiredPermissions: [UserPermission.TICKETS_READ],
      },
      {
        id: AppScreen.TEMPLATE_MANAGEMENT,
        val: AppScreen.TEMPLATE_MANAGEMENT,
        title: 'Template Management',
        icon: <GridIcon fill={Colors.light.hereworksBlue500} />,
        selectedIcon: <GridIcon
          color={'#fff'}
          fill={'#fff'}
        />,
        testID: 'template-mngmt-section-btn',
        requiredPermissions: [UserPermission.TEMPLATES_READ],
      },
      {
        id: AppScreen.TEMPLATE_MANAGEMENTV2,
        val: AppScreen.TEMPLATE_MANAGEMENTV2,
        title: 'Template Mgmt V2',
        icon: <GridIcon fill={Colors.light.hereworksBlue500} />,
        selectedIcon: <GridIcon
          color={'#fff'}
          fill={'#fff'}
        />,
        beta: true,
        testID: 'template-mngmt-section-btn',
        requiredPermissions: [UserPermission.TEMPLATES_READ],
      },
      {
        id: AppScreen.ASSET_MANAGEMENT,
        val: AppScreen.ASSET_MANAGEMENT,
        title: 'Asset Management',
        icon: <GridIcon />,
        selectedIcon: <GridIcon color={'#fff'} />,
        beta: true,
        testID: 'asset-mngmt-section-btn',
        requiredPermissions: [UserPermission.ASSETS_READ],
      },
      {
        id: AppScreen.ANALYTICS,
        val: AppScreen.ANALYTICS,
        title: 'Solve Analytics',
        icon: <SolveIcon24 />,
        selectedIcon: <SolveIcon24 color={'#fff'} />,
        testID: 'solve-analytics-section-btn',
        requiredPermissions: [UserPermission.SOLVE_ANALYTICS_READ],
      },
      {
        id: AppScreen.USER_MANAGEMENT,
        val: AppScreen.USER_MANAGEMENT,
        title: 'User Management',
        icon: <PeopleIcon24 />,
        selectedIcon: <PeopleIcon24 color={'#fff'} />,
        testID: 'user-mgmt-section-btn',
        requiredPermissions: [UserPermission.USER_GROUPS_READ, UserPermission.USERS_READ],
      },
      {
        id: `${AppScreen.USER_MANAGEMENT}V2`,
        val: `${AppScreen.USER_MANAGEMENT}V2`,
        title: 'User Management V2',
        icon: <PeopleIcon24 />,
        selectedIcon: <PeopleIcon24 color={'#fff'} />,
        testID: 'user-mgmt-v2-section-btn',
        beta: true,
        requiredPermissions: [UserPermission.USER_GROUPS_READ, UserPermission.USERS_READ],
      },
      {
        id: AppScreen.PDFViewer,
        val: AppScreen.PDFViewer,
        title: 'PDF Viewer',
        icon: <GridIcon fill={Colors.light.hereworksBlue500} />,
        selectedIcon: <GridIcon
          color={'#fff'}
          fill={'#fff'}
        />,
        testID: 'pdf-viewer-section-btn',
        beta: true,
      },
    ],
  },
  {
    isExpanded: true,
    category_name: 'BUILDING',
    subcategory: [
      {
        id: AppScreen.MAP,
        val: AppScreen.MAP,
        icon: <MapIcon />,
        selectedIcon: <MapIcon color={'#fff'} />,
        testID: 'map-section-btn',
      },
      {
        id: AppScreen.SMART_GUARD,
        val: AppScreen.MAP,
        route: 'smartguardtour',
        title: 'Smart Guard Tour',
        icon: <SensorIcon24 color={Colors.light.hereworksBlue400} />,
        selectedIcon: <SensorIcon24 color={'#fff'} />,
        testID: 'smart-guard-tour-btn',
        indented: true,
      },
      {
        id: AppScreen.ENERGY,
        val: AppScreen.ENERGY,
        title: HappyScoreCategoryTitles[HappyScoreCategory.ENERGY],
        icon: <BoltIconDesktop />,
        selectedIcon: <BoltIconDesktop color={'#fff'} />,
        testID: 'energy-waste-section-btn',
      },
      {
        id: 8,
        val: 'BuildingOccupancy',
        title: HappyScoreCategoryTitles[HappyScoreCategory.OCCUPANCY],
        icon: <OccupancyIcon />,
        selectedIcon: <OccupancyIcon color={'#fff'} />,
        testID: 'building-occupancy-section-btn',
      },
      {
        id: AppScreen.EXTERNAL_ENVIRONMENT,
        val: AppScreen.EXTERNAL_ENVIRONMENT,
        title: HappyScoreCategoryTitles[HappyScoreCategory.EXTERNAL_ENVIRONMENT],
        icon: <SunIcon />,
        selectedIcon: <SunIcon color={'#fff'} />,
        testID: 'external-env-section-btn',
      },
      {
        id: AppScreen.INTERNAL_ENVIRONMENT,
        val: AppScreen.INTERNAL_ENVIRONMENT,
        title: HappyScoreCategoryTitles[HappyScoreCategory.INTERNAL_ENVIRONMENT],
        icon: <ThermometerIcon />,
        selectedIcon: <ThermometerIcon color={'#fff'} />,
        testID: 'internal-env-section-btn',
      },
      {
        id: AppScreen.IT,
        val: AppScreen.IT,
        title: HappyScoreCategoryTitles[HappyScoreCategory.IT],
        icon: <MonitorIcon />,
        selectedIcon: <MonitorIcon color={'#fff'} />,
        testID: 'it-section-btn',
      },
      {
        id: 4,
        val: 'OccupantSatisfaction',
        title: HappyScoreCategoryTitles[HappyScoreCategory.SATISFACTION],
        icon: <SmileIcon />,
        selectedIcon: <SmileIcon color={'#fff'} />,
        testID: 'occupant-satisfaction-section-btn',
      },
      {
        id: AppScreen.AMENITIES,
        val: AppScreen.AMENITIES,
        title: HappyScoreCategoryTitles[HappyScoreCategory.AMENITIES],
        icon: <CheckCircle />,
        selectedIcon: <CheckCircle color={'#fff'} />,
        testID: 'amenities-section-btn',
      },
      {
        id: AppScreen.ERGONOMICS,
        val: AppScreen.ERGONOMICS,
        title: HappyScoreCategoryTitles[HappyScoreCategory.ERGONOMICS],
        icon: <ChairIconDesktop />,
        selectedIcon: <ChairIconDesktop color={'#fff'} />,
        testID: 'ergonomics-section-btn',
      },
    ],
  },
  {
    isExpanded: true,
    category_name: 'SUPPORT',
    subcategory: [
      {
        id: AppScreen.SUPPORT,
        val: AppScreen.SUPPORT,
        title: 'Request Support',
        icon: <SupportIcon />,
        selectedIcon: <SupportIcon color={'#fff'} />,
        testID: 'request-support-section-btn',
      },
    ],
  },
];

const SOLVE_CONTENT = [
  {
    isExpanded: true,
    category_name: 'MANAGEMENT',
    subcategory: [
      {
        id: AppScreen.HAPPY_CHAT_V2,
        val: AppScreen.HAPPY_CHAT_V2,
        title: 'Insights Agent',
        icon: <BotIcon />,
        selectedIcon: <BotIcon color={'#fff'} />,
        featureFlag: 'chatbot',
        testID: 'happy-chat-v2-section-btn',
        beta: true,
      },
      {
        id: AppScreen.TICKETS,
        val: AppScreen.TICKETS,
        icon: <FlagIcon />,
        selectedIcon: <FlagIcon color={'#fff'} />,
        testID: 'tickets-section-btn',
      },
      {
        id: AppScreen.TICKETS_V2,
        val: AppScreen.TICKETS_V2,
        title: 'Tickets V2',
        icon: <FlagIcon />,
        selectedIcon: <FlagIcon color={'#fff'} />,
        beta: true,
        testID: 'tickets-v2-section-btn',
      },
      {
        id: AppScreen.TEMPLATE_MANAGEMENT,
        val: AppScreen.TEMPLATE_MANAGEMENT,
        title: 'Template Management',
        icon: <GridIcon fill={Colors.light.hereworksBlue500} />,
        selectedIcon: <GridIcon
          color={'#fff'}
          fill={'#fff'}
        />,
        testID: 'template-mngmt-section-btn',
      },
      {
        id: AppScreen.TEMPLATE_MANAGEMENTV2,
        val: AppScreen.TEMPLATE_MANAGEMENTV2,
        title: 'Template Mgmt V2',
        icon: <GridIcon fill={Colors.light.hereworksBlue500} />,
        selectedIcon: <GridIcon
          color={'#fff'}
          fill={'#fff'}
        />,
        beta: true,
        testID: 'template-mngmt-section-btn',
      },
      {
        id: AppScreen.ASSET_MANAGEMENT,
        val: AppScreen.ASSET_MANAGEMENT,
        title: 'Asset Management',
        icon: <GridIcon />,
        selectedIcon: <GridIcon color={'#fff'} />,
        beta: true,
        testID: 'asset-mngmt-section-btn',
      },
      {
        id: AppScreen.ANALYTICS,
        val: AppScreen.ANALYTICS,
        title: 'Solve Analytics',
        icon: <SolveIcon24 />,
        selectedIcon: <SolveIcon24 color={'#fff'} />,
        testID: 'solve-analytics-section-btn',
      },
      {
        id: AppScreen.USER_MANAGEMENT,
        val: AppScreen.USER_MANAGEMENT,
        title: 'User Management',
        icon: <PeopleIcon24 />,
        selectedIcon: <PeopleIcon24 color={'#fff'} />,
        testID: 'user-mgmt-section-btn',
      },
    ],
  },
  {
    isExpanded: true,
    category_name: 'BUILDING',
    subcategory: [
      {
        id: AppScreen.MAP,
        val: AppScreen.MAP,
        icon: <MapIcon />,
        selectedIcon: <MapIcon color={'#fff'} />,
        testID: 'map-section-btn',
      },
      {
        id: AppScreen.HAPPY_CHAT,
        val: AppScreen.HAPPY_CHAT,
        title: 'Happy Chat',
        icon: <BotIcon />,
        selectedIcon: <BotIcon color={'#fff'} />,
        beta: true,
        testID: 'happy-chat-section-btn',
      },
    ],
  },
  {
    isExpanded: true,
    category_name: 'SUPPORT',
    subcategory: [
      {
        id: AppScreen.SUPPORT,
        val: AppScreen.SUPPORT,
        title: 'Request Support',
        icon: <SupportIcon />,
        selectedIcon: <SupportIcon color={'#fff'} />,
        testID: 'request-support-section-btn',
      },
    ],
  },
];
